import { useEffect, useMemo, useState } from "react";
import { sortAges } from "@/lib/ageOrder";
import { pitchLabel } from "@/lib/pitchLabel";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw, Pencil, XCircle, UserCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// Admin → Bookings has two sub-tabs:
//   1. "All bookings"  — the historic full-table view (unchanged)
//   2. "Team clashes"  — pairs of confirmed future bookings that share a team
//                        and overlap in time (new /admin/bookings/team-clashes).
//
// Both are read-only tables with per-row "Amend" (deep-links into MyBookings
// which opens the BookingDetailDialog) and "Cancel" (fires DELETE /bookings/{id}
// with a confirm). Amend uses ?booking_id=X so admins never need to hunt the row.
export function BookingsTab({ bookings, onChanged }) {
  const [tab, setTab] = useState("all");
  const [cleaning, setCleaning] = useState(false);
  const reassign = async () => {
    if (!window.confirm("Transfer every admin-owned team booking to its team manager?")) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/bookings/reassign-owners");
      toast.success(`${data.moved} booking${data.moved === 1 ? "" : "s"} moved to team managers${data.teams_without_owner ? ` · ${data.teams_without_owner} team(s) have no manager set` : ""}`);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Clean-up failed"); }
    finally { setCleaning(false); }
  };
  return (
    <div className="space-y-4" data-testid="admin-bookings-panel">
      <div className="flex items-center gap-2 border-b border-border pb-2">
        <button type="button" onClick={reassign} disabled={cleaning} data-testid="admin-bookings-reassign-owners"
          className="ml-auto order-last text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] transition-colors inline-flex items-center gap-1.5 disabled:opacity-40">
          <UserCheck className="w-3 h-3" /> {cleaning ? "Transferring…" : "Transfer admin bookings to managers"}
        </button>
        <button
          type="button"
          onClick={() => setTab("all")}
          data-testid="admin-bookings-subtab-all"
          className={`text-[11px] tracking-widest uppercase px-3 py-1.5 rounded transition-colors ${
            tab === "all"
              ? "bg-[#83B68B]/20 text-[#83B68B] border border-[#83B68B]/50"
              : "opacity-70 hover:opacity-100 border border-transparent"
          }`}
        >
          All bookings ({bookings.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("clashes")}
          data-testid="admin-bookings-subtab-clashes"
          className={`text-[11px] tracking-widest uppercase px-3 py-1.5 rounded transition-colors inline-flex items-center gap-1.5 ${
            tab === "clashes"
              ? "bg-amber-500/20 text-amber-400 border border-amber-500/50"
              : "opacity-70 hover:opacity-100 border border-transparent"
          }`}
        >
          Team clashes / duplicates
        </button>
      </div>
      {tab === "all" ? <AllBookings bookings={bookings} /> : <TeamClashes />}
    </div>
  );
}

function AllBookings({ bookings }) {
  return (
    <div className="card-tactical rounded-md overflow-x-auto" data-testid="admin-bookings-all">
      <table className="w-full text-sm">
        <thead className="bg-muted text-[10px] tracking-widest uppercase opacity-60">
          <tr>
            <th className="text-left px-4 py-3">Date</th>
            <th className="text-left px-4 py-3">Time</th>
            <th className="text-left px-4 py-3">Pitch</th>
            <th className="text-left px-4 py-3">Type</th>
            <th className="text-left px-4 py-3">Opponent</th>
            <th className="text-left px-4 py-3">Booked by</th>
            <th className="text-left px-4 py-3">Ref</th>
            <th className="text-left px-4 py-3">Fee</th>
            <th className="text-left px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map(b => (
            <tr key={b.id} className="border-t border-border">
              <td className="px-4 py-3">{format(parseISO(b.booking_date), "d MMM yyyy")}</td>
              <td className="px-4 py-3 opacity-80">{b.start_hour.toString().padStart(2, "0")}:00 — {b.end_hour.toString().padStart(2, "0")}:00</td>
              <td className="px-4 py-3">{pitchLabel(b)}</td>
              <td className="px-4 py-3 text-xs"><span className={`px-2 py-1 rounded ${b.session_type === "game" ? "bg-[#83B68B]/20 text-[#83B68B]" : "bg-muted opacity-70"}`}>{b.session_type || "training"}{b.match_type ? ` · ${b.match_type}` : ""}</span></td>
              <td className="px-4 py-3 opacity-80">{b.opponent || "—"}</td>
              <td className="px-4 py-3 opacity-80">{b.user_name}</td>
              <td className="px-4 py-3 opacity-80">{b.referee_name || "—"}</td>
              <td className="px-4 py-3 opacity-80">{b.expected_referee_fee != null ? `£${Number(b.expected_referee_fee).toFixed(2)}` : "—"}</td>
              <td className="px-4 py-3"><span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded ${b.status === "confirmed" ? "bg-[#83B68B]/20 text-[#83B68B]" : "bg-red-500/20 text-red-500"}`}>{b.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TeamClashes() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ count: 0, clashes: [] });
  const [busyId, setBusyId] = useState(null);
  const [includePast, setIncludePast] = useState(false);
  const [teamF, setTeamF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [venueF, setVenueF] = useState("all");
  const [ageF, setAgeF] = useState("all");
  const [venues, setVenues] = useState([]);
  useEffect(() => { api.get("/venues").then(r => setVenues(r.data)).catch(() => {}); }, []);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings/team-clashes", { params: { include_past: includePast } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load team clashes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [includePast]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamOpts = useMemo(() => {
    const m = new Map();
    data.clashes.forEach(c => c.teams.forEach(t => m.set(t.id, t.name)));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);
  const venueOpts = useMemo(() => venues.map(v => v.name || v.short_code).filter(Boolean).sort(), [venues]);
  const ageOpts = useMemo(() => sortAges([...new Set(data.clashes.flatMap(c => [c.a.age_group, c.b.age_group]).filter(Boolean))]), [data]);
  const sideVenue = s => s.venue_name || s.venue_short_code;
  const filtered = useMemo(() => data.clashes.filter(c =>
    (teamF === "all" || c.teams.some(t => t.id === teamF))
    && (typeF === "all" || [c.a, c.b].some(s => (s.session_type || "training") === typeF))
    && (venueF === "all" || [c.a, c.b].some(s => sideVenue(s) === venueF))
    && (ageF === "all" || [c.a, c.b].some(s => s.age_group === ageF))
    && (!dateFrom || c.date >= dateFrom) && (!dateTo || c.date <= dateTo)
  ), [data, teamF, typeF, venueF, ageF, dateFrom, dateTo]);
  const hasFilter = teamF !== "all" || typeF !== "all" || venueF !== "all" || ageF !== "all" || dateFrom || dateTo;

  const cancel = async (id, ref) => {
    if (!window.confirm(`Cancel booking ${ref}? This can't be undone.`)) return;
    setBusyId(id);
    try {
      await api.delete(`/bookings/${id}`);
      toast.success(`Cancelled ${ref}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't cancel that booking");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="opacity-50 font-display tracking-widest text-sm" data-testid="admin-clashes-loading">LOADING…</div>;
  }
  return (
    <div className="space-y-4" data-testid="admin-bookings-clashes">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400">Team clashes · duplicate bookings per team per day</div>
          <p className="text-sm opacity-70 mt-1">
            Pairs of confirmed bookings where the same team appears twice on the same day (any venue, any time).
            {" "}Amend or cancel one side of each pair to clear the duplicate.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="admin-clashes-refresh"
          className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="admin-clashes-filters">
        <Select value={teamF} onValueChange={setTeamF}><SelectTrigger className="h-9 w-44" data-testid="clashes-filter-team"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All teams</SelectItem>{teamOpts.map(([id, n]) => <SelectItem key={id} value={id}>{n}</SelectItem>)}</SelectContent></Select>
        <Select value={typeF} onValueChange={setTypeF}><SelectTrigger className="h-9 w-36" data-testid="clashes-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All sessions</SelectItem><SelectItem value="game">Games</SelectItem><SelectItem value="training">Training</SelectItem></SelectContent></Select>
        <Select value={ageF} onValueChange={setAgeF}><SelectTrigger className="h-9 w-40" data-testid="clashes-filter-age"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All year groups</SelectItem>{ageOpts.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
        <Select value={venueF} onValueChange={setVenueF}><SelectTrigger className="h-9 w-44" data-testid="clashes-filter-venue"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All venues</SelectItem>{venueOpts.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="clashes-filter-from" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-40" data-testid="clashes-filter-to" />
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none"><input type="checkbox" checked={includePast} onChange={e => setIncludePast(e.target.checked)} className="accent-[#83B68B]" data-testid="clashes-include-past" /> Include past dates</label>
        {hasFilter && <button type="button" onClick={() => { setTeamF("all"); setTypeF("all"); setVenueF("all"); setAgeF("all"); setDateFrom(""); setDateTo(""); }} data-testid="clashes-clear-filters" className="text-[10px] tracking-widest uppercase opacity-60 hover:text-[#83B68B]">Clear</button>}
        <span className="text-[10px] tracking-widest uppercase opacity-50 ml-auto" data-testid="clashes-count">{filtered.length} of {data.clashes.length}</span>
      </div>
      {filtered.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-70" data-testid="admin-clashes-empty">
          <div className="text-4xl mb-2">✓</div>
          <div className="font-display font-bold text-lg">{hasFilter ? "No clashes match these filters" : "No team clashes"}</div>
          <div className="text-xs opacity-70 mt-1">{hasFilter ? "Try widening the filters." : "Every future booking respects the one-team-per-day rule."}</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-clashes-list">
          {filtered.map((c, i) => (
            <ClashRow
              key={`${c.a.id}-${c.b.id}`}
              c={c}
              busy={busyId === c.a.id || busyId === c.b.id}
              onCancel={cancel}
              testIdx={i}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ClashRow({ c, busy, onCancel, testIdx }) {
  return (
    <div className="card-tactical rounded-md p-4 border border-amber-500/30 bg-amber-500/5" data-testid={`clash-row-${testIdx}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <div className="text-sm">
          <span className="font-display font-bold text-base">{format(parseISO(c.date), "EEE d MMM yyyy")}</span>
          <span className="mx-2 opacity-40">·</span>
          <span className="text-[10px] tracking-widest uppercase text-amber-400">Conflicting team{c.teams.length > 1 ? "s" : ""}:</span>
          {c.teams.map((t, i) => (
            <span key={t.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-amber-500/15 border border-amber-500/40 text-amber-300 ml-1.5">
              {t.name}
            </span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ClashSide side={c.a} busy={busy} onCancel={onCancel} label="A" testId={`clash-${testIdx}-a`} />
        <ClashSide side={c.b} busy={busy} onCancel={onCancel} label="B" testId={`clash-${testIdx}-b`} />
      </div>
    </div>
  );
}

function ClashSide({ side, busy, onCancel, label, testId }) {
  const ref = pitchLabel(side) || side.id.slice(0, 6);
  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid={testId}>
      <div className="text-[9px] tracking-widest uppercase opacity-50 mb-1">Booking {label}</div>
      <div className="font-medium">{ref}</div>
      <div className="text-xs opacity-70 mt-1">
        {side.start}–{side.end} · {side.session_type || "training"}
      </div>
      <div className="text-xs opacity-70 mt-0.5">
        {side.team_name && <span className="opacity-90">{side.team_name}</span>}
        {side.team_name && side.user_name && <span className="opacity-40"> · </span>}
        {side.user_name && <span>Booked by {side.user_name}</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to={`/app/bookings?booking_id=${side.id}`}
          data-testid={`${testId}-amend`}
          className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5"
        >
          <Pencil className="w-3 h-3" /> Amend
        </Link>
        <button
          onClick={() => onCancel(side.id, ref)}
          disabled={busy}
          data-testid={`${testId}-cancel`}
          className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-red-500/40 hover:bg-red-500/10 text-red-400 rounded inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <XCircle className="w-3 h-3" /> Cancel
        </button>
      </div>
    </div>
  );
}
